import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import RaidPlanner from './RaidPlanner';
import { table } from '../../lib/manifest';
import { reloadIfStale } from '../../lib/stale';
import { useHub } from '../context';
import type { Hub } from '../hub';
import type { ArtDone, ArtJob } from '../mapart.worker';
import { BUNGIE_NOTE, Message } from '../ui';
import { CloudLayer } from '../clouds';

/**
 * Port of the app's Maps tab: the game's drawn destination maps with fog and drifting clouds, every point that can be
 * pointed at (landing zones, vendors, lost sectors, chests, collectables, lore, patrols) with a card each and whether
 * it has been found, and the way into the Raid Planner. Map pictures and data used with the owners' permission.
 */

interface MapPoint { kind: string; title: string; sub: string; area: string; desc: string; shot?: string; spot?: number[]; done?: boolean; x: number; y: number; trail?: number[][] }
interface MapDef { slug: string; destinationHash: number; map: any }

const LEGEND: [string, string][] = [['fast-travel', 'Landing zone'], ['lost-sector', 'Lost sector'], ['vendor', 'Vendor'], ['chest', 'Chest'], ['collect', 'Collectable'], ['lore', 'Lore'], ['patrol', 'Patrol'], ['portal', 'Other point'], ['found', 'Found']];
const LAYOUT_KINDS: Record<string, string> = { 'fast-travel': 'Landing zone', vendor: 'Vendor', 'lost-sector': 'Lost sector', portal: 'Portal', 'ascendant-challenge': 'Ascendant Challenge', forge: 'Forge', dungeon: 'Dungeon', landmark: 'Landmark' };
const legendKind = (k: string) => (['fast-travel', 'lost-sector', 'vendor', 'chest', 'collect', 'lore', 'patrol'].includes(k) ? k : 'portal');
const HIDDEN_KEY = 'd2c.maps.hidden';

interface MapData { maps: Record<string, MapDef>; nodes: any }
let dataJob: Promise<MapData> | null = null;
const loadData = (): Promise<MapData> => (dataJob ??= Promise.all([
  fetch('/maps/maps.json').then((r) => r.json()),
  fetch('/maps/nodes.json').then((r) => r.json()).catch(() => null),
]).then(([maps, nodes]) => ({ maps, nodes } as MapData)).catch((e) => { dataJob = null; throw e; }));

// ---------------- the map pictures, tuned as the app does (see mapart.worker.ts) ----------------

let artWorker: Worker | null | undefined;
const artWaiting = new Map<string, (src: string) => void>();
const artDone = new Map<string, Promise<string>>();
/** The picture with its lines lifted, as a local address; the picture as stored where the browser can't do it. */
function tunedArt(url: string, tint?: string): Promise<string> {
  const key = `${url}|${tint ?? ''}`;
  let job = artDone.get(key);
  if (job) return job;
  if (artWorker === undefined) {
    try {
      artWorker = typeof OffscreenCanvas === 'undefined' ? null : new Worker(new URL('../mapart.worker.ts', import.meta.url), { type: 'module' });
      // its file gone (a tab from before the last deploy): the waiting pictures show as they are, and the page reloads
      artWorker?.addEventListener('error', () => {
        artWorker = null;
        for (const [k, done] of artWaiting) done(k.split('|')[0]);
        artWaiting.clear(); artDone.clear();
        void reloadIfStale();
      });
      artWorker?.addEventListener('message', (e: MessageEvent<ArtDone>) => {
        const k = `${e.data.url}|${e.data.tint ?? ''}`;
        artWaiting.get(k)?.(e.data.blob ? URL.createObjectURL(e.data.blob) : e.data.url);
        artWaiting.delete(k);
      });
    } catch { artWorker = null; }
  }
  if (!artWorker) return Promise.resolve(url);
  job = new Promise<string>((done) => { artWaiting.set(key, done); artWorker!.postMessage({ url, tint } satisfies ArtJob); });
  artDone.set(key, job);
  return job;
}

/** One map piece, tuned; it says when its picture is ready to show (the map is revealed once all of them are). */
function MapPiece({ src, tint, style, opacity, onReady }: { src: string; tint?: string; style: CSSProperties; opacity: number; onReady: () => void }) {
  const [shown, setShown] = useState<string>();
  useEffect(() => { let live = true; void tunedArt(src, tint).then((s) => { if (live) setShown(s); }); return () => { live = false; }; }, [src, tint]);
  return shown ? <img src={shown} alt="" draggable={false} decoding="async" className="g-mappiece" style={{ ...style, ['--o' as string]: opacity }} onLoad={onReady} onError={onReady} /> : null;
}

// Places in the layout data that are wrong: the Pale Heart's third piece sits 297 px too far left (its art matches the
// second piece's exactly 3000 px along, measured 2026-10-24; at 1803 the shared strip showed twice, out of step, in the
// middle of the map). Only applied while the data still says 1803.
const PIECE_FIX: Record<string, { from: number; x: number }> = { 'map_pale-heart-3.png': { from: 1803, x: 2100 } };

// clip and shared: top, right, bottom, left; a shared side meets a neighbour piece and is not feathered (maps.css)
interface Placed { file: string; tint?: string; opacity: number; l: number; t: number; w: number; h: number; clip: [number, number, number, number]; shared: [boolean, boolean, boolean, boolean] }

/**
 * Where each piece goes on the canvas. Pieces side by side that overlap (the Pale Heart's three) are cut to meet in the
 * middle of the shared strip, which both pieces draw: drawn twice, the strip came out brighter, with each piece's
 * square edge showing. Pieces that only touch (Kepler's four) reach 1 px over, so no hairline of the dark shows
 * between them when scaled. A small piece lying inside a bigger one (the EDZ's Volundr) is left as it is.
 */
function placePieces(nodes: any[], w: number, h: number): Placed[] {
  const out: Placed[] = nodes.map((p) => {
    const file = String(p.image).split('/').pop()!;
    const fix = PIECE_FIX[file];
    const x = fix && p.x === fix.from ? fix.x : p.x;
    return { file, tint: p.color, opacity: p.opacity ?? 1, l: (w - 1920) / 2 + x, t: (h - 1080) / 2 + p.y, w: p.width, h: p.height, clip: [0, 0, 0, 0], shared: [false, false, false, false] };
  });
  const span = (a0: number, a1: number, b0: number, b1: number) => Math.max(0, Math.min(a1, b1) - Math.max(a0, b0));
  for (const a of out) for (const b of out) {
    if (a === b) continue;
    // b to the right of a, side by side (rows of one strip: nearly all their height shared; the Shattered Throne's two
    // pieces only meet at a corner and are left alone), b not inside a
    if (b.l > a.l && b.l <= a.l + a.w + 0.5 && b.l + b.w > a.l + a.w && span(a.t, a.t + a.h, b.t, b.t + b.h) > 0.9 * Math.max(a.h, b.h)) {
      const o = a.l + a.w - b.l;
      if (o <= 0.5) a.clip[1] = -1;                 // touching: a reaches 1 px into b
      else { a.clip[1] = o / 2 - 0.5; b.clip[3] = o / 2 - 0.5; }   // each gives up half the strip (1 px overlap kept)
      a.shared[1] = b.shared[3] = true;
    }
    // b below a, the same way
    if (b.t > a.t && b.t <= a.t + a.h + 0.5 && b.t + b.h > a.t + a.h && span(a.l, a.l + a.w, b.l, b.l + b.w) > 0.9 * Math.max(a.w, b.w)) {
      const o = a.t + a.h - b.t;
      if (o <= 0.5) a.clip[2] = -1;
      else { a.clip[2] = o / 2 - 0.5; b.clip[0] = o / 2 - 0.5; }
      a.shared[2] = b.shared[0] = true;
    }
  }
  return out;
}

// the outer fifth of each side eases out (its baked-in clouds ended in a visible straight line at 5%)
const EASE_IN = 'transparent, rgba(0,0,0,.2) 5%, rgba(0,0,0,.55) 10%, rgba(0,0,0,.85) 15%, #000 20%';
const EASE_OUT = '#000 80%, rgba(0,0,0,.85) 85%, rgba(0,0,0,.55) 90%, rgba(0,0,0,.2) 95%, transparent';
const fadeAxis = (dir: string, startShared: boolean, endShared: boolean) => `linear-gradient(${dir}, ${startShared ? '#000' : EASE_IN}, ${endShared ? '#000' : EASE_OUT})`;

/**
 * A placed piece's box, cut and feathering: a negative cut grows the box that much on that side (a stretch of 1 px in
 * thousands); only the sides facing out are feathered (feathered where pieces meet, Kepler showed a dark cross through
 * its middle and the Pale Heart dark bands, where both pieces faded out together).
 */
function pieceStyle(p: Placed): CSSProperties {
  const [ct, cr, cb, cl] = p.clip, [st, sr, sb, sl] = p.shared;
  const grow = (c: number) => (c < 0 ? -c : 0);
  const mask = `${fadeAxis('90deg', sl, sr)}, ${fadeAxis('180deg', st, sb)}`;
  const style: CSSProperties = { left: p.l, top: p.t, width: p.w + grow(cr), height: p.h + grow(cb), maskImage: mask, WebkitMaskImage: mask };
  if (ct > 0 || cr > 0 || cb > 0 || cl > 0) style.clipPath = `inset(${Math.max(0, ct)}px ${Math.max(0, cr)}px ${Math.max(0, cb)}px ${Math.max(0, cl)}px)`;
  return style;
}

/** "Region Chests" to "Region Chest", "Feathers of Light" to "Feather of Light". */
const oneOf = (many: string) => { const i = many.indexOf('s of '); return i > 0 ? many.slice(0, i) + many.slice(i + 1) : many.endsWith('s') ? many.slice(0, -1) : many; };
const plainWords = (s?: string) => (s ?? '').replace(/\*\*/g, '').replace(/_/g, '').trim();
const titleCase = (s: string) => s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function pointsFor(slug: string, map: any, nodes: any, destination: string, vendorNames: Record<string, any>, hub: Hub | undefined, charId?: string): MapPoint[] {
  const points: MapPoint[] = [];
  const details: Record<string, any> = nodes?.details ?? {};
  const shot = (d: any) => (d?.s ? `/maps/${d.s}` : undefined);
  for (const b of map.bubbles ?? []) {
    const place: string = b.name ?? '';
    for (const n of b.nodes ?? []) {
      const what = LAYOUT_KINDS[n.type];
      if (!what) continue;
      // the words and picture for this point, where there are some: a vendor by its number, the rest by the point's
      const more = n.vendorHash ? Object.values<any>(details).find((d) => d.v === n.vendorHash) : n.hash ? details[n.hash] : undefined;
      const title = more?.n || vendorNames[n.vendorHash]?.displayProperties?.name || place || what;
      const area = [...new Set([place, destination].filter((s) => s && s !== title))].join(', ');
      points.push({ kind: n.type, title, sub: what, area, desc: plainWords(more?.d), shot: shot(more), x: n.x, y: n.y });
    }
  }
  const here = nodes?.maps?.[slug];
  for (const p of here?.points ?? []) {
    const listInfo = nodes?.lists?.[p.l];
    const one = oneOf(listInfo?.title ?? 'Collectable');
    const name: string = p.n ?? '';
    // Bungie names the numbered ones "70. Cosmodrome": shown as "Region Chest 70"
    const numbered = !name || (/^\d/.test(name) && name.includes('. '));
    const kind = p.l === 'LostSectors' ? 'lost-sector' : ['RegionChests', 'EuropaStasisSealedChests', 'KeplerAbilityChests'].includes(p.l) ? 'chest'
      : ['LoreLunasLost', 'LoreInquisitionOfTheDamned'].includes(p.l) ? 'lore' : 'collect';
    let done: boolean | undefined;
    if (hub) {
      if (p.c && listInfo?.id) done = hub.ticked(listInfo.id, p.c);
      else if (p.r) done = hub.record(p.r, charId)?.done;
    }
    points.push({ kind, title: numbered ? `${one} ${Math.round(p.num ?? 0)}` : name, sub: one, area: p.a ?? destination, desc: plainWords(p.d), shot: shot(p), spot: p.hl?.length === 2 ? p.hl : undefined, done, x: p.x, y: p.y });
  }
  for (const p of here?.extra ?? []) {
    const icon: string = p.i ?? '';
    const sort = icon.startsWith('patrol-') && icon !== 'patrol-random' ? `Patrol  ·  ${titleCase(icon.slice(7))}` : icon === 'deepsight' ? 'Deepsight' : 'Patrol';
    const trail = (p.cr ?? []).filter((run: number[]) => run.length >= 4);
    points.push({ kind: 'patrol', title: p.n || 'Patrol', sub: sort, area: destination, desc: plainWords(p.d), shot: shot(p), spot: p.hl?.length === 2 ? p.hl : undefined, x: p.x, y: p.y, trail: trail.length ? trail : undefined });
  }
  return points;
}

export default function MapsTab() {
  const h = useHub();
  const [data, setData] = useState<MapData>();
  const [error, setError] = useState(false);
  const [names, setNames] = useState<Record<string, any>>({});
  const [vendors, setVendors] = useState<Record<string, any>>({});
  const [pick, setPick] = useState('');
  const [hidden, setHidden] = useState<Set<string>>(() => { try { return new Set(JSON.parse(localStorage.getItem(HIDDEN_KEY) ?? '[]')); } catch { return new Set(); } });
  const [params, setParams] = useSearchParams();
  const planner = params.has('planner');
  const setPlanner = (on: boolean) => setParams(on ? { planner: '1' } : {});
  const [full, setFull] = useState(false);
  const frame = useRef<HTMLDivElement>(null);
  useEffect(() => {
    loadData().then(setData).catch(() => setError(true));
    table<any>('DestinyDestinationDefinition').then(setNames).catch(() => undefined);
    table<any>('DestinyVendorDefinition').then(setVendors).catch(() => undefined);
  }, []);
  useEffect(() => {
    const on = () => setFull(document.fullscreenElement === frame.current);
    document.addEventListener('fullscreenchange', on);
    return () => document.removeEventListener('fullscreenchange', on);
  }, []);
  if (planner) return <RaidPlanner onClose={() => setPlanner(false)} />;
  if (error) return <Message title="Maps" text="The map files could not be loaded." />;
  if (!data) return <Message title="Maps" text="Loading the maps..." />;
  const list = Object.values(data.maps);
  const name = (m: MapDef) => names[m.destinationHash]?.displayProperties?.name || titleCase(m.slug);
  const open = data.maps[pick] ?? list[0];
  const toggle = (k: string) => setHidden((s) => {
    const x = new Set(s);
    if (!x.delete(k)) x.add(k);
    try { localStorage.setItem(HIDDEN_KEY, JSON.stringify([...x])); } catch { /* storage blocked */ }
    return x;
  });
  const goFull = () => { if (document.fullscreenElement) void document.exitFullscreen(); else void frame.current?.requestFullscreen?.(); };

  return (
    <div className={`g-mapframe ${full ? 'full' : ''}`} ref={frame}>
      {open && (
        <MapView key={open.slug} def={open} hidden={hidden}
          points={pointsFor(open.slug, open.map, data.nodes, name(open), vendors, h.hub, h.char?.id)} />
      )}
      <div className="g-mapglass g-maplist">
        <div className="g-maphead">DESTINATION</div>
        {list.map((m) => <button key={m.slug} type="button" className={m.slug === open?.slug ? 'on' : ''} onClick={() => setPick(m.slug)}>{name(m)}</button>)}
        <div className="g-maphead" style={{ marginTop: 14 }}>RAIDS AND DUNGEONS</div>
        <button type="button" className="planner" onClick={() => setPlanner(true)} title="Plan a raid or dungeon: each encounter's map, roles and callouts">Raid Planner</button>
      </div>
      <div className="g-mapplate">{(open ? name(open) : '').toUpperCase()}</div>
      <div className="g-maptop">
        <button type="button" className={`g-fullpill ${full ? 'on' : ''}`} onClick={goFull} title={full ? 'Back to the normal view (Esc)' : 'The map over the whole screen'}>{full ? '⤡  Exit full screen' : '⤢  Full screen'}</button>
      </div>
      <div className="g-mapglass g-maplegend">
        {(
          <>
            {LEGEND.map(([k, label]) => (
              <button key={k} type="button" style={{ opacity: hidden.has(k) ? 0.32 : 1 }} onClick={() => toggle(k)} title={k === 'found' ? 'Show or hide the points you have already found' : `Show or hide: ${label.toLowerCase()}`}>
                {k === 'found' ? <span className="g-found">✓</span> : <Marker kind={k} size={22} />}{label}
              </button>
            ))}
            <span className="sep" />
            <span className="how">Drag to move  ·  wheel or pinch to zoom</span>
          </>
        )}
      </div>
      <span className="g-mapglass g-mapcredit">Maps and map data: the work of Braytech (bray.tech)<small>{BUNGIE_NOTE}</small></span>
    </div>
  );
}

/** A point drawn in the game's manner: landing zone (ring and arrowhead), lost sector (arches), vendor (badge), chest, lore, collectable, patrol, anything else (diamond). */
export function Marker({ kind, size }: { kind: string; size: number }) {
  const s = (d: string, fill = false, w = 1.6) => <path d={d} fill={fill ? 'rgba(255,255,255,0.95)' : 'none'} stroke="rgba(255,255,255,0.95)" strokeWidth={w} strokeLinejoin="round" />;
  let art: ReactNode;
  switch (kind) {
    case 'fast-travel': art = <><circle cx="12" cy="12" r="11" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.7" strokeDasharray="1 3.2" />{s('M12,4 A8,8 0 1 0 12.01,4 Z', false, 1.2)}{s('M8.2,10 L15.8,10 L12,15.6 Z', true, 0.5)}</>; break;
    case 'lost-sector': art = s('M5,19 V12 A7,7 0 0 1 19,12 V19 M9,19 V13 A3,3 0 0 1 15,13 V19'); break;
    case 'chest': art = s('M6.5,9 H17.5 V17 H6.5 Z M6.5,12 H17.5 M11,12 V14.2 H13 V12', false, 1.4); break;
    case 'lore': art = s('M12,8.2 C10.4,7 8.2,6.8 6,7.2 V16.6 C8.2,16.2 10.4,16.5 12,17.6 C13.6,16.5 15.8,16.2 18,16.6 V7.2 C15.8,6.8 13.6,7 12,8.2 Z M12,8.2 V17.6', false, 1.3); break;
    case 'collect': art = <>{s('M12,5.5 L17.6,8.8 V15.2 L12,18.5 L6.4,15.2 V8.8 Z', false, 1.4)}<circle cx="12" cy="12" r="1.8" fill="rgba(255,255,255,0.95)" /></>; break;
    case 'patrol': art = s('M12,7.5 L16.6,15.6 H7.4 Z', true, 0.6); break;
    case 'vendor': art = <>{s('M7,5 H17 V14 L12,19.5 L7,14 Z', true, 0.5)}<circle cx="12" cy="10.5" r="2" fill="#0a0f16" /></>; break;
    default: art = s('M12,5 L19,12 L12,19 L5,12 Z');
  }
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className="g-marker" aria-hidden="true">
      <circle cx="12" cy="12" r={kind === 'fast-travel' ? 8 : 12} fill={kind === 'fast-travel' ? 'rgba(8,10,12,0.94)' : 'rgba(10,15,22,0.5)'} />
      {art}
    </svg>
  );
}

// ---------------- one drawn map ----------------

function MapView({ def, points, hidden }: { def: MapDef; points: MapPoint[]; hidden: Set<string> }) {
  const m = def.map;
  const w: number = m.width, h: number = m.height;
  const box = useRef<HTMLDivElement>(null);
  const [view, setView] = useState({ x: 0, y: 0, k: 0.85 });
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [hover, setHover] = useState<{ p: MapPoint; x: number; y: number }>();
  const [card, setCard] = useState<{ p: MapPoint; x: number; y: number }>();
  const press = useRef<{ x: number; y: number; vx: number; vy: number; moved: boolean } | null>(null);
  // phones: the fingers on the map, and a two-finger pinch's start (the gap between them, their middle, the view then)
  const fingers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ gap: number; mx: number; my: number; v: { x: number; y: number; k: number } } | null>(null);
  const twoFingers = () => {
    const [a, b] = [...fingers.current.values()];
    return { gap: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2 };
  };

  // never zoomed out past the map's own edges; moved, it may rest up to half the view past each edge, so any spot,
  // even one at the very edge, can be brought to the middle of the screen at any zoom (the owner: the edge blocked
  // centring things when zoomed out); the mirrored backdrop fills the space beyond
  const clamp = (v: { x: number; y: number; k: number }, pw = size.w, ph = size.h) => {
    if (!pw || !ph) return v;
    const k = Math.min(2.5, Math.max(Math.max(pw / w, ph / h), v.k));
    const mx = pw / 2, my = ph / 2;
    const x = Math.min(mx, Math.max(pw - w * k - mx, v.x));
    const y = Math.min(my, Math.max(ph - h * k - my, v.y));
    return { x, y, k };
  };
  // past the map's edge a drag still moves it, but less and less (a third of the way, at most 140 px), and on letting go
  // it glides back inside (like a phone's list), instead of stopping dead at the edge
  const stretch = (v: { x: number; y: number; k: number }) => {
    const c = clamp(v), give = (o: number) => Math.sign(o) * Math.min(140, Math.abs(o) * 0.35);
    return { k: c.k, x: c.x + give(v.x - c.x), y: c.y + give(v.y - c.y) };
  };
  const settling = useRef(0);
  // the wheel: each notch sets where the view is going (goal) and the view glides there over a few frames, instead of
  // jumping a step per notch; so the last notch's pull to the map's middle is a glide too, not a snap (the owner)
  const viewNow = useRef(view); viewNow.current = view;
  const goal = useRef<{ x: number; y: number; k: number } | null>(null);
  const glideTo = (g: { x: number; y: number; k: number }, tau = 90) => {
    goal.current = g;
    cancelAnimationFrame(settling.current);
    // timed, not counted in frames: on a 360 Hz screen a share per frame finished in under 0.1 s (a snap again);
    // this eases out over about 0.4 s whatever the refresh rate (tau: the time constant in ms; the arrival is slower)
    let last = performance.now();
    const step = (now: number) => setView((v) => {
      const t = goal.current;
      if (!t) return v;
      const a = 1 - Math.exp(-Math.max(0, now - last) / tau); last = now;
      const k = v.k * Math.pow(t.k / v.k, a), x = v.x + (t.x - v.x) * a, y = v.y + (t.y - v.y) * a;
      if (Math.abs(t.k - k) < 0.0005 && Math.abs(t.x - x) < 0.5 && Math.abs(t.y - y) < 0.5) { goal.current = null; return t; }
      settling.current = requestAnimationFrame(step);
      return { k, x, y };
    });
    settling.current = requestAnimationFrame(step);
  };
  const settle = () => {
    goal.current = null;
    cancelAnimationFrame(settling.current);
    const step = () => setView((v) => {
      const c = clamp(v), nx = v.x + (c.x - v.x) * 0.2, ny = v.y + (c.y - v.y) * 0.2;
      if (Math.abs(c.x - nx) < 0.5 && Math.abs(c.y - ny) < 0.5) return c;
      settling.current = requestAnimationFrame(step);
      return { k: v.k, x: nx, y: ny };
    });
    settling.current = requestAnimationFrame(step);
  };
  useEffect(() => () => cancelAnimationFrame(settling.current), []);
  // the first look, where the camera settles once the map is revealed
  const firstLook = useRef<{ x: number; y: number; k: number } | null>(null);
  useEffect(() => {
    let placed = false;
    const ro = new ResizeObserver(() => {
      const r = box.current!.getBoundingClientRect();
      if (r.width < 100) return;
      setSize({ w: r.width, h: r.height });
      if (!placed) {
        // first look: near life size, on the place the layout names as the middle. Its two numbers are stored
        // up-then-across (x = how far up from the bottom, y = how far across), the reverse of how they read.
        // While the art is being made ready the camera waits a little further out, and comes in as it shows.
        placed = true;
        const cx = m.center ? m.center.y : w / 2, cy = m.center ? h - m.center.x : h / 2;
        firstLook.current = clamp({ k: 0.85, x: r.width / 2 - cx * 0.85, y: r.height / 2 - cy * 0.85 }, r.width, r.height);
        const f = firstLook.current, k = f.k * 0.94, mx = r.width / 2, my = r.height / 2;
        setView(clamp({ k, x: mx - ((mx - f.x) / f.k) * k, y: my - ((my - f.y) / f.k) * k }, r.width, r.height));
      } else setView((v) => clamp(v, r.width, r.height));
    });
    ro.observe(box.current!);
    return () => ro.disconnect();
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const el = box.current!;
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      touched.current = true;
      const r = el.getBoundingClientRect();
      const px = e.clientX - r.left, py = e.clientY - r.top;
      setCard(undefined); setHover(undefined);
      // from where the view is already going, so quick notches add up
      const v = goal.current ?? viewNow.current;
      // as the app: one wheel notch (about 100 px) is one 1.2x step; smooth wheels and touchpads send many small
      // movements, each now a small part of a step instead of a whole one (which zoomed several times too fast)
      const dist = e.deltaMode === 1 ? e.deltaY * 33 : e.deltaMode === 2 ? e.deltaY * 800 : e.deltaY;
      const k = Math.min(2.5, v.k * Math.pow(1.2, -Math.max(-3, Math.min(3, dist / 100))));
      const at = clamp({ k, x: px - ((px - v.x) / v.k) * k, y: py - ((py - v.y) / v.k) * k }, r.width, r.height);
      glideTo(k < v.k ? clamp(towardMiddle(at, r.width, r.height), r.width, r.height) : at);
    };
    el.addEventListener('wheel', wheel, { passive: false });
    return () => el.removeEventListener('wheel', wheel);
  });

  const colours = (m.layers ?? []).filter((l: any) => l.type === 'background').flatMap((l: any) => l.nodes ?? []).map((n: any) => n.color);
  const top = colours[0] ?? '#6aaf80', bottom = colours[1] ?? '#6283bf';
  const pieces = (m.layers ?? []).filter((l: any) => l.type === 'map').flatMap((l: any) => l.nodes ?? []);
  const placed = useMemo(() => placePieces(pieces, w, h), [m]);   // eslint-disable-line react-hooks/exhaustive-deps
  // The reveal: the art shows when every piece is ready (it used to pop in a piece at a time, the names and points
  // over an empty backdrop before it), all together, the camera easing in; then the names and points, then the sky
  // (maps.css). A quiet line says how far along it is meanwhile; after 8 s it shows whatever is there.
  const [ready, setReady] = useState<Set<string>>(() => new Set());
  const [late, setLate] = useState(false);
  useEffect(() => { const t = window.setTimeout(() => setLate(true), 8000); return () => window.clearTimeout(t); }, []);
  const revealed = late || placed.every((p) => ready.has(p.file));
  const touched = useRef(false);   // dragged or zoomed while it was loading: the camera is theirs, it does not ease in
  useEffect(() => {
    if (revealed && firstLook.current && !touched.current) glideTo(firstLook.current, 300);
  }, [revealed, size.w > 0]);   // eslint-disable-line react-hooks/exhaustive-deps
  // the middle of the drawn map (its pieces), in canvas pixels: where a zoomed-out view settles
  let artL = Infinity, artT = Infinity, artR = -Infinity, artB = -Infinity;
  for (const p of placed) {
    artL = Math.min(artL, p.l); artT = Math.min(artT, p.t); artR = Math.max(artR, p.l + p.w); artB = Math.max(artB, p.t + p.h);
  }
  const art = Number.isFinite(artL) ? { x: (artL + artR) / 2, y: (artT + artB) / 2 } : { x: w / 2, y: h / 2 };
  // the map's own colour (its biggest piece's tint, else its backdrop's) mixed into the dark round it, so past the edge
  // the fade lands on a dark of the map's colour (red-brown for Nessus, green-grey for the EDZ), not one grey for all
  const mainTint = [...pieces].sort((a: any, b: any) => b.width * b.height - a.width * a.height).find((p: any) => p.color)?.color ?? top;
  const surround = `color-mix(in srgb, ${mainTint} 9%, #0b0c0c)`;
  // Zooming out follows the pointer, but the further out, the more the view glides to the map's middle, all the way
  // at the widest zoom: a square map in a wide window still overflows top and bottom there, and the view used to stop
  // wherever the zoom left it, pressed against an edge (the European Dead Zone ended up at its top).
  const towardMiddle = (v: { x: number; y: number; k: number }, pw: number, ph: number) => {
    const kmin = Math.max(pw / w, ph / h);
    const f = Math.min(1, Math.max(0, (v.k - kmin) / Math.max(0.0001, 0.85 - kmin)));
    const cx = pw / 2 - art.x * v.k, cy = ph / 2 - art.y * v.k;
    return { k: v.k, x: cx + (v.x - cx) * f, y: cy + (v.y - cy) * f };
  };
  const titles = useMemo(() => {
    const t: { name: string; region: boolean; x: number; y: number }[] = [];
    for (const b of m.bubbles ?? []) for (const n of b.nodes ?? []) if (n.type === 'title' && b.name) t.push({ name: b.name, region: b.type === 'region', x: n.x, y: n.y });
    return t;
  }, [m]);
  // one wind for the whole map, from its name (so maps differ): mostly sideways, a little up or down (clouds.tsx)
  const windSeed = useMemo(() => [...def.slug].reduce((a, ch) => (a * 31 + ch.charCodeAt(0)) % 2147483647, 7), [def.slug]);
  const windLeft = windSeed % 2 === 0, windTilt = ((windSeed >> 3) % 7 - 3) * 0.08;
  const inv = 1 / view.k;   // names and markers keep one size on screen however far the map is zoomed
  const shown = points.filter((p) => !hidden.has(legendKind(p.kind)) && !(p.done === true && hidden.has('found')));
  const at = (p: MapPoint) => ({ x: view.x + (w / 2 + p.x) * view.k, y: view.y + (h / 2 - p.y) * view.k });
  const half = (p: MapPoint) => (p.kind === 'fast-travel' ? 20 : p.kind === 'patrol' ? 8 : 12);

  return (
    <div ref={box} className={`g-mapview ${revealed ? 'shown' : ''}`} style={{ background: surround }}
      onPointerDown={(e) => {
        touched.current = true;
        fingers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        // the pointer is only held by the map once a drag or pinch starts: a plain click or tap still reaches the point
        // under it and opens its card (held from the press, the release went to the map and the card never opened)
        if (fingers.current.size === 2) { for (const id of fingers.current.keys()) { try { box.current?.setPointerCapture(id); } catch { /* gone */ } } press.current = null; pinch.current = { ...twoFingers(), v: view }; setCard(undefined); setHover(undefined); return; }
        goal.current = null; cancelAnimationFrame(settling.current);   // caught while gliding: it stays where it is under the finger
        press.current = { x: e.clientX, y: e.clientY, vx: view.x, vy: view.y, moved: false };
      }}
      onPointerMove={(e) => {
        if (fingers.current.has(e.pointerId)) fingers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const p = pinch.current;
        if (p && fingers.current.size >= 2) {
          // zoom by how far the fingers spread, around the point between them, and move with them
          const now = twoFingers(), r = box.current!.getBoundingClientRect();
          const k = Math.min(2.5, p.v.k * (now.gap / p.gap));
          const sx = p.mx - r.left, sy = p.my - r.top;
          const at = clamp({ k, x: now.mx - r.left - ((sx - p.v.x) / p.v.k) * k, y: now.my - r.top - ((sy - p.v.y) / p.v.k) * k });
          setView(k < p.v.k ? clamp(towardMiddle(at, r.width, r.height)) : at);
          return;
        }
        const d = press.current;
        if (!d) return;
        const dx = e.clientX - d.x, dy = e.clientY - d.y;
        if (!d.moved && Math.abs(dx) + Math.abs(dy) < 6) return;   // not a drag yet: it may be a click
        if (!d.moved) { try { box.current?.setPointerCapture(e.pointerId); } catch { /* gone */ } }
        d.moved = true;
        const next = { k: view.k, x: d.vx + dx, y: d.vy + dy };
        setCard(undefined); setHover(undefined);
        setView(stretch(next));
      }}
      onPointerUp={(e) => {
        fingers.current.delete(e.pointerId);
        if (pinch.current) { if (fingers.current.size < 2) pinch.current = null; press.current = null; return; }
        const d = press.current;
        press.current = null;
        if (d?.moved) settle();
        if (d && !d.moved && !(e.target as HTMLElement).closest('.g-mappoint')) setCard(undefined);
      }}
      onPointerCancel={(e) => { fingers.current.delete(e.pointerId); pinch.current = null; press.current = null; settle(); }}>
      <div className="g-mapcanvas" style={{ width: w, height: h, transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})` }}>
        {/* the backdrop (its tint and grid art), fading out at its edges into the frame's plain dark, so past the map's
            edge there is calm darkness: the mirrored copies round it repeated the backdrop's cloudy marks as reflected
            pairs (a "double cloud") with a seam where they met */}
        <div className="g-mapbackdrop" style={{ left: 0, top: 0, width: w, height: h,
          background: `linear-gradient(180deg, ${top}12, ${top}04 45%, ${bottom}04 55%, ${bottom}12), ${surround}` }}>
          <img src="/maps/background_top.png" alt="" className="g-mapbg" style={{ top: 0, width: w, height: h / 2 }} />
          <img src="/maps/background_bottom.png" alt="" className="g-mapbg" style={{ top: h / 2, width: w, height: h / 2 }} />
        </div>
        {placed.map((p) => <MapPiece key={p.file} src={`/maps/${p.file}`} tint={p.tint} opacity={p.opacity} style={pieceStyle(p)}
          onReady={() => setReady((s) => (s.has(p.file) ? s : new Set(s).add(p.file)))} />)}
        {/* the clouds: one seamless layer set drifting on the map's wind, over the art and under the names (clouds.tsx) */}
        <CloudLayer view={view} size={size} windLeft={windLeft} tilt={windTilt} sky={def.slug === 'the-moon' ? 'space' : 'clouds'} />   {/* the Moon has no air: satellites, not clouds */}
        {titles.map((t, i) => (
          <span key={`t${i}`} className={`g-maptitle ${t.region ? 'region' : ''}`} style={{ left: w / 2 + t.x, top: h / 2 - t.y, transform: `translate(-50%, -50%) scale(${inv})` }}>{t.name}</span>
        ))}
        {hover?.p.trail && (
          <svg className="g-maptrail" width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
            {hover.p.trail.map((run, i) => <polyline key={i} points={run.reduce<string[]>((a, v, j) => (j % 2 ? a : [...a, `${w / 2 + v},${h / 2 - run[j + 1]}`]), []).join(' ')} style={{ strokeWidth: 2 * inv }} />)}
          </svg>
        )}
        {shown.map((p, i) => (
          <span key={`p${i}`} className="g-mappoint" style={{ left: w / 2 + p.x, top: h / 2 - p.y, transform: `translate(-50%, -50%) scale(${inv * (hover?.p === p ? 1.35 : 1)})`, opacity: hover?.p === p ? 1 : p.done ? 0.36 : p.kind === 'patrol' ? 0.7 : 1 }}
            onPointerEnter={() => { if (!press.current?.moved && !card) { const a = at(p); setHover({ p, x: a.x, y: a.y }); } }}
            onPointerLeave={() => setHover(undefined)}
            onPointerUp={() => { if (press.current && !press.current.moved) { const a = at(p); setHover(undefined); setCard({ p, x: a.x, y: a.y }); } }}>
            <Marker kind={p.kind} size={half(p) * 2} />
          </span>
        ))}
      </div>
      <div className="g-mapvignette" />
      <div className={`g-maploading ${revealed ? 'done' : ''}`} aria-live="polite">
        <span>Charting the map</span>
        <i style={{ ['--p' as string]: placed.length ? placed.filter((p) => ready.has(p.file)).length / placed.length : 0 }} />
      </div>
      {hover && !card && <span className="g-maptip" style={{ left: hover.x + half(hover.p) * 1.35 + 8, top: hover.y - 13 }}>{hover.p.title === hover.p.sub ? hover.p.title : `${hover.p.title}  ·  ${hover.p.sub}`}</span>}
      {card && <MapCard p={card.p} x={card.x} y={card.y} box={size} close={() => setCard(undefined)} />}
    </div>
  );
}

/** The card a point opens: its symbol, name and kind on a dark band, its picture (a ring on the marked spot), its words, where it is and whether it has been found. */
function MapCard({ p, x, y, box, close }: { p: MapPoint; x: number; y: number; box: { w: number; h: number }; close: () => void }) {
  const wide = 380;
  // beside the point, on the side with room, and never past the view's edges
  const left = Math.min(Math.max(12, x + 30 + wide <= box.w - 12 ? x + 30 : x - 30 - wide), Math.max(12, box.w - wide - 12));
  const top = Math.min(Math.max(12, y - 70), Math.max(12, box.h - 420));
  return (
    <div className="g-mapcard" style={{ left, top, width: wide }} onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()}>
      <div className="head">
        <span className="sym"><Marker kind={p.kind} size={p.kind === 'fast-travel' ? 46 : 36} /></span>
        <span className="words"><b>{p.title.toUpperCase()}</b><span>{p.sub}</span></span>
        <button type="button" onClick={close} aria-label="Close">✕</button>
      </div>
      {p.shot && (
        <div className="shot">
          <img src={p.shot} alt="" />
          {p.spot && <span className="spot" style={{ left: `${p.spot[0]}%`, top: `${p.spot[1]}%` }} />}
        </div>
      )}
      {p.desc && <p className="desc">{p.desc}</p>}
      <div className="foot">
        <span>{p.area}</span>
        {p.done !== undefined && <span className={`badge ${p.done ? 'on' : ''}`}>{p.done ? 'Found' : 'Not found yet'}</span>}
      </div>
    </div>
  );
}
